import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { SERIES_PAIR, INK, FONT } from './vizTokens';

/**
 * Two series over time, as inline SVG.
 *
 * One y-axis, always. Two measures on two scales is the single most common way
 * a chart lies, and both series here are counts of applications, so they belong
 * on the same scale anyway — if approvals trail submissions, that gap is the
 * point of the chart and a second axis would hide it.
 *
 * Two series also means a legend is mandatory: identity must never rest on
 * colour-matching alone. The single-series bar charts elsewhere correctly have
 * none, because their heading already names what is plotted.
 */
const PAD = { top: 16, right: 16, bottom: 28, left: 36 };
const H = 200;

const LineChart = ({ points, seriesA = 'Submitted', seriesB = 'Approved', emptyText = 'No activity yet' }) => {
  const [hover, setHover] = useState(null);

  if (!points || points.length === 0) {
    return (
      <Typography sx={{ ...FONT, color: INK.muted, fontSize: '13px', py: 2 }}>
        {emptyText}
      </Typography>
    );
  }

  // The SVG keeps its own coordinate space and the container scrolls, rather
  // than scaling a viewBox — preserveAspectRatio="none" would stretch the stroke
  // weights and the axis text with it.
  //
  // Horizontal spacing tightens as points multiply. A fixed 56px per point suits
  // a dozen buckets and is catastrophic for thirty: the SVG grows to 1680px
  // inside a ~640px panel, so two thirds of the window sits scrolled out of
  // sight and the chart silently shows a third of itself.
  const xStep = points.length > 20 ? 19 : points.length > 12 ? 34 : 56;
  const W = Math.max(320, points.length * xStep);
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const max = Math.max(1, ...points.map((p) => Math.max(p.a, p.b)));
  // Round the axis to a clean number so the ticks read as values, not as
  // whatever the data happened to peak at.
  const step = max <= 4 ? 1 : Math.ceil(max / 4);
  const top = step * 4;

  const x = (i) => PAD.left + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const y = (v) => PAD.top + plotH - (v / top) * plotH;

  const path = (key) => points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p[key])}`).join(' ');

  const ticks = [0, 1, 2, 3, 4].map((i) => i * step);
  // Label roughly six x positions; more than that and they collide.
  const labelEvery = Math.max(1, Math.ceil(points.length / 6));

  const series = [
    { key: 'a', name: seriesA, color: SERIES_PAIR[0] },
    { key: 'b', name: seriesB, color: SERIES_PAIR[1] },
  ];

  return (
    <Box>
      {/* Legend — not optional at two series. */}
      <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
        {series.map((s) => (
          <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 2.5, borderRadius: '2px', backgroundColor: s.color }} />
            {/* Text wears ink, never the series colour — the swatch beside it
                carries identity. */}
            <Typography sx={{ ...FONT, fontSize: '12px', color: INK.secondary }}>{s.name}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ overflowX: 'auto', minWidth: 0, maxWidth: '100%' }}>
        <svg
          width={W}
          height={H}
          role="img"
          aria-label={`${seriesA} against ${seriesB} over time`}
          onMouseLeave={() => setHover(null)}
        >
          {/* Gridlines: hairline, solid, recessive. Never dashed. */}
          {ticks.map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} stroke={INK.grid} strokeWidth="1" />
              <text
                x={PAD.left - 8} y={y(t) + 4} textAnchor="end"
                fill={INK.muted} fontSize="11" style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {t}
              </text>
            </g>
          ))}

          {series.map((s) => (
            <path
              key={s.key}
              d={path(s.key)}
              fill="none"
              stroke={s.color}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          {/* End markers only — a dot on every point is noise at this density.
              Each carries a 2px surface ring so it stays legible where the two
              lines cross. */}
          {series.map((s) => {
            const i = points.length - 1;
            return (
              <circle
                key={s.key}
                cx={x(i)} cy={y(points[i][s.key])} r="4"
                fill={s.color} stroke={INK.surface} strokeWidth="2"
              />
            );
          })}

          {/* Hit targets span the full column height, so hovering does not
              require landing on the 2px line itself. */}
          {points.map((p, i) => (
            <rect
              key={p.label}
              x={x(i) - plotW / (points.length * 2 || 1)}
              y={PAD.top}
              width={Math.max(12, plotW / (points.length || 1))}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
          ))}

          {hover != null && (
            <line
              x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotH}
              stroke={INK.baseline} strokeWidth="1"
            />
          )}

          {points.map((p, i) => (
            i % labelEvery === 0 ? (
              <text
                key={p.label} x={x(i)} y={H - 8} textAnchor="middle"
                fill={INK.muted} fontSize="11"
              >
                {p.label}
              </text>
            ) : null
          ))}
        </svg>
      </Box>

      {/* The tooltip is DOM rather than SVG so it can never be clipped by the
          plot bounds. */}
      <Box sx={{ minHeight: 22, mt: 0.5 }}>
        {hover != null && (
          <Typography sx={{ ...FONT, fontSize: '12px', color: INK.secondary }}>
            <strong style={{ color: INK.primary }}>{points[hover].label}</strong>
            {' — '}{seriesA} {points[hover].a}, {seriesB} {points[hover].b}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default LineChart;
